import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, Save, Upload } from 'lucide-react';

/** SiteIdentityTab — brand-identity + contact fields.
 *  All state lives in AdminSettingsPage; this is a pure presentational component.
 */
export function SiteIdentityTab({ siteIdentity, setSiteIdentity, saveSiteIdentity, uploadImage, saving }) {
  return (
    <Card className="bg-[#041A3B] border-white/10">
      <CardHeader>
        <CardTitle className="text-white">Site Identity</CardTitle>
        <CardDescription className="text-[#A0B4CC]">Configure your brand identity and contact information</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-white">Site Name</Label>
            <Input
              value={siteIdentity.site_name}
              onChange={(e) => setSiteIdentity({ ...siteIdentity, site_name: e.target.value })}
              className="bg-[#000B18] border-white/20 text-white"
              placeholder="All Global Access"
              data-testid="site-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white">Tagline</Label>
            <Input
              value={siteIdentity.tagline}
              onChange={(e) => setSiteIdentity({ ...siteIdentity, tagline: e.target.value })}
              className="bg-[#000B18] border-white/20 text-white"
              placeholder="Unlimited Opportunities. Unified Access."
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {[
            { key: 'logo_url',     label: 'Full Logo' },
            { key: 'icon_url',     label: 'Icon / Square Logo' },
            { key: 'favicon_url',  label: 'Favicon' },
            { key: 'og_image_url', label: 'OG Social Share Image' },
          ].map(item => (
            <div key={item.key} className="space-y-2">
              <Label className="text-white">{item.label}</Label>
              <div className="flex items-center gap-3">
                {siteIdentity[item.key] && (
                  <div className="w-12 h-12 rounded-lg bg-[#000B18] border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img
                      src={siteIdentity[item.key]}
                      alt={item.label}
                      className="w-full h-full object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                <Input
                  value={siteIdentity[item.key] || ''}
                  onChange={(e) => setSiteIdentity({ ...siteIdentity, [item.key]: e.target.value })}
                  className="bg-[#000B18] border-white/20 text-white flex-1"
                  placeholder="URL or upload..."
                  data-testid={`${item.key}-input`}
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) uploadImage(e.target.files[0], item.key);
                    }}
                  />
                  <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center hover:bg-[#00E5FF]/20 transition-colors">
                    <Upload className="w-4 h-4 text-[#00E5FF]" />
                  </div>
                </label>
              </div>
              {item.key === 'og_image_url' && (
                <p className="text-[#5C7599] text-xs">Recommended: 1200x630px. Used for social media previews when sharing links.</p>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <Label className="text-white">Description</Label>
          <Textarea
            value={siteIdentity.description || ''}
            onChange={(e) => setSiteIdentity({ ...siteIdentity, description: e.target.value })}
            className="bg-[#000B18] border-white/20 text-white"
            placeholder="Short description for SEO and social sharing"
            rows={2}
            data-testid="site-description-input"
          />
        </div>

        <div className="border-t border-white/10 pt-6">
          <h4 className="font-medium text-white mb-4">Contact Information</h4>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-white">Contact Email</Label>
              <Input
                type="email"
                value={siteIdentity.contact_email || ''}
                onChange={(e) => setSiteIdentity({ ...siteIdentity, contact_email: e.target.value })}
                className="bg-[#000B18] border-white/20 text-white"
                placeholder="contact@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Contact Phone</Label>
              <Input
                value={siteIdentity.contact_phone || ''}
                onChange={(e) => setSiteIdentity({ ...siteIdentity, contact_phone: e.target.value })}
                className="bg-[#000B18] border-white/20 text-white"
                placeholder="+63 XXX XXX XXXX"
              />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <Label className="text-white">Address</Label>
            <Textarea
              value={siteIdentity.address || ''}
              onChange={(e) => setSiteIdentity({ ...siteIdentity, address: e.target.value })}
              className="bg-[#000B18] border-white/20 text-white"
              placeholder="123 Main St, City, Country"
              rows={2}
            />
          </div>
        </div>

        <Button onClick={saveSiteIdentity} disabled={saving} className="btn-primary" data-testid="save-site-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </CardContent>
    </Card>
  );
}

export default SiteIdentityTab;
